import { isLoggedIn } from '../api/auth';
import { FIBER_KO, FIELD_KO } from '../vocab';
import { facetLabel } from './Facets';
import { Icon } from './Icon';
import {
  AdminSkeleton, CardsSkeleton, DetailSkeleton, GridSkeleton, PageSkeleton, RailSkeleton, SavedRowsSkeleton,
} from './Skeleton';

/**
 * 화면 청크를 내려받는 동안(처음 들어가는 화면) · 세션을 확인하는 동안 그리는 골격 (9회차 m44 · 루브릭 R3-4).
 * 예전에는 글자만 있는 「불러오는 중…」 이 0.7~1.2초 보였다. 각 화면이 데이터를 기다릴 때 그리는 골격과 같은 틀 · 같은 글자 ·
 * 같은 칸 수라, 청크 → 데이터 → 카드로 이어지는 동안 머리 · 레일 자리가 움직이지 않는다.
 * 바닥글은 main 최소 높이(styles.css)로 첫 화면 밖에 있어 짧은 골격에도 들썩이지 않는다.
 */
function savedSize(): number {
  const n = Number(new URLSearchParams(window.location.search).get('size'));
  if (Number.isInteger(n) && n >= 1 && n <= 100) return n;
  try {
    const s = Number(localStorage.getItem('aitex.pageSize'));
    return [24, 48, 96].includes(s) ? s : 24;
  } catch { return 24; }
}
function savedView(): 'grid' | 'list' {
  try { return localStorage.getItem('aitex.view') === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
}

/** 주소의 조건 → 받은 뒤 레일(Facets)에 붙을 칩 글자, 같은 순서(Browse 의 railChips 와 같다 — 14회차 m64) */
const SINCE_KO: Record<string, string> = { today: '오늘', '7d': '최근 7일', '30d': '최근 30일' };
function railChipsFromUrl(): string[] {
  const p = new URLSearchParams(window.location.search);
  const out: string[] = [];
  const since = p.get('since');
  if (since && SINCE_KO[since]) out.push(`기간 · ${SINCE_KO[since]}`);
  const fibers = [...new Set((p.get('material') ?? '').split(',').map((x) => x.trim().toLowerCase()).filter((x) => FIBER_KO[x]))];
  for (const f of fibers) out.push(`소재 · ${FIBER_KO[f]}`);
  // 비로그인은 제조사 · 재고 조건을 쓰지 않는다(화면이 빼고 보인다 — Codex 8차 B7) — 골격 칩도 같게
  const supply = isLoggedIn();
  for (const k of ['color', 'pattern', 'manufacturer'] as const) {
    const v = p.get(k);
    if (v && (k !== 'manufacturer' || supply)) out.push(`${FIELD_KO[k]} · ${facetLabel(k, v)}`);
  }
  if (supply && p.get('in_stock') === 'true') out.push('재고 보유');
  return out;
}

/** 전체 원단(Browse) 첫 화면과 같은 틀 — 레일 골격 · 「전체 원단」 제목 · 정렬/개수/보기 칸 · 카드 골격 */
function BrowseSkeleton() {
  const size = savedSize();
  const view = savedView();
  return (
    <div className="browse-page page page-grid" aria-busy="true">
      <div className="page-rail">
        <div className="sheet-bar"><span className="btn sm sheet-open skel-btn" aria-hidden="true"><Icon name="sliders" size={16} /> 필터</span></div>
        <div className="rail-desk"><RailSkeleton chips={railChipsFromUrl()} /></div>
      </div>
      <section className="page-main browse-main" aria-labelledby="browse-title-skel">
        <header className="browse-head">
          <div className="browse-title-row">
            <h1 id="browse-title-skel">전체 원단</h1>
            <span className="res-cnt skel-line w-24" aria-hidden="true" />
          </div>
          <div className="browse-tools skel-tools" aria-hidden="true">
            <span className="skel-ctl" /><span className="skel-ctl" />
            <span className="view-toggle">
              <span className="icon-btn" aria-pressed={view === 'grid'}><Icon name="grid" size={18} /></span>
              <span className="icon-btn" aria-pressed={view === 'list'}><Icon name="list" size={18} /></span>
            </span>
          </div>
        </header>
        <GridSkeleton n={Math.min(size, 24)} view={view} label="전체 원단을 불러오는 중…" />
      </section>
    </div>
  );
}

export default function RouteSkeleton({ pathname }: { pathname: string }) {
  if (pathname.startsWith('/browse')) return <BrowseSkeleton />;
  // 13회차: 원단 상세도 지연 청크 — 청크를 받는 동안 상세가 데이터를 기다릴 때와 같은 골격(사진 칸 · 제목 · 요약 줄)
  if (pathname.startsWith('/fabric/')) return <DetailSkeleton />;
  if (pathname === '/collections') {
    return (
      <div className="coll-page page offset" aria-busy="true">
        <header className="coll-head">
          <h1>내 컬렉션</h1>
          <span className="sp" />
          <span className="btn primary sm skel-btn" aria-hidden="true"><Icon name="plus" size={16} /> 새 컬렉션</span>
        </header>
        <p className="coll-lead"><span className="skel-line w-60" /></p>
        <CardsSkeleton n={4} label="컬렉션 목록을 불러오는 중…" />
      </div>
    );
  }
  if (pathname.startsWith('/collections/')) {
    return (
      <div className="coll-page page offset" aria-busy="true">
        <div className="coll-crumb"><span className="skel-line w-24" /></div>
        <header className="coll-head"><span className="skel-line h-title w-40" /></header>
        <GridSkeleton n={12} label="컬렉션을 불러오는 중…" />
      </div>
    );
  }
  if (pathname.startsWith('/saved-searches')) {
    return (
      <div className="coll-page page offset saved-page" aria-busy="true">
        <header className="coll-head"><h1>저장한 검색</h1></header>
        <p className="coll-lead"><span className="skel-line w-80" /></p>
        <SavedRowsSkeleton label="저장한 검색을 불러오는 중…" />
      </div>
    );
  }
  if (pathname.startsWith('/admin')) {
    // 관리 틀(메뉴)도 지연 청크라 같은 자리에 메뉴 줄 골격
    return (
      <div className="page page-grid admin-shell">
        <div className="page-rail side-nav" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="skel-line w-80 skel-nav" />)}
        </div>
        <div className="page-main"><AdminSkeleton /></div>
      </div>
    );
  }
  return <PageSkeleton />;
}
